import { FC, useEffect, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { getUserList } from 'api/users';
import { User } from 'api/users/types';
import { useGlobalState } from 'context/GlobalState/GlobalStateContext';
import { extractUsers } from 'hooks/users/utils';
import { AuthProviderProps, AxiosResponseType } from 'types';
import { AuthContext, setAuthUserToLocalStorage, setUserToLocalStorage } from './AuthContext';

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, addUser] = useState({} as AxiosResponseType);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [deniedEmail, setDeniedEmail] = useState<string>('');

  const getAuthenticatedEmail = (currentUser: AxiosResponseType): string => {
    return (
      currentUser?.attributes?.email ||
      currentUser?.signInUserSession?.idToken?.payload?.email ||
      currentUser?.challengeParam?.userAttributes?.email ||
      ''
    );
  };

  // Authenticated via Google, but not authorised for the data tool (no user
  // record, or the API rejected them because they are not in the access group).
  const denyAccess = (email: string) => {
    setDeniedEmail(email);
    setAccessDenied(true);
  };

  const setUser = (currentUser: AxiosResponseType) => {
    setIsUserLoading(true);
    setAccessDenied(false);
    setUserToLocalStorage(currentUser);
    addUser(currentUser);

    const email = getAuthenticatedEmail(currentUser);

    if (!email) {
      denyAccess('');
      setIsUserLoading(false);
      return;
    }

    getUserList({ user_email: email })
      .then((userPayload) => {
        // Resolve the app user by email. Federated (Google SSO) users get a new
        // Cognito `sub`, so permissions are matched on email rather than user id.
        const users = extractUsers(userPayload) as User[];
        const appUser =
          users.find((user) => (user.email || '').toLowerCase() === email.toLowerCase()) || null;

        if (!appUser) {
          // Signed in, but this account has no data-tool access.
          denyAccess(email);
          return;
        }

        const enrichedUser = {
          ...currentUser,
          ...appUser,
          appUser,
          permissionsSchema: appUser.permissions,
        };

        addUser(enrichedUser);
        setUserToLocalStorage(enrichedUser);
        setAuthUserToLocalStorage(appUser);
      })
      .catch(() => {
        // API rejected the request (e.g. not in the access group) or was
        // unreachable — treat as no access rather than a broken empty app.
        denyAccess(email);
      })
      .finally(() => {
        setIsUserLoading(false);
      });
  };

  // Catch the Cognito Hosted UI (Google SSO) redirect back into the app and
  // hydrate the authenticated user + permissions.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signIn' || payload.event === 'cognitoHostedUI') {
        Auth.currentAuthenticatedUser()
          .then((currentUser) => setUser(currentUser))
          .catch(() => undefined);
      }
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Define the resendCode function
  const resendCode = async (username: string) => {
    try {
      await Auth.resendSignUp(username);
    } catch (error) {
      console.error('Error resending confirmation code:', error);
    }
  };

  const { dispatch } = useGlobalState();

  const verifyCode = async (username: string, code: string) => {
    try {
      await Auth.confirmSignUp(username, code);
      dispatch({
        type: 'VERIFY_CODE',
      });
    } catch (error) {
      console.error(error);
    }
  };

  const verifyMFA = async (code: string, mfaType: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null | undefined) => {
    try {
      return await Auth.confirmSignIn(user, code, mfaType);
    } catch (error) {
      console.error(error);
    }
  };

  const passwordRecovery = async (email: string) => {
    try {
      await Auth.forgotPassword(email);
    } catch (error) {
      alert(error);
    }
  };

  const passwordReset = async (email: string, code: string, newPassword: string) => {
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
    } catch (error) {
      alert(error);
    }
  };

  // Include resendCode in the value passed to AuthContext.Provider
  return (
    <AuthContext.Provider
      value={{
        user,
        isUserLoading,
        accessDenied,
        deniedEmail,
        setUser,
        resendCode,
        verifyMFA,
        verifyCode,
        passwordRecovery,
        passwordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
